import React, { useEffect, useMemo, useState, useRef } from "react";
import { Sparkles, Loader2, Check } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const KARATS = ["22K", "18K", "14K", "9K"];
const GOLD_COLORS = [
  { value: "Yellow", swatch: "#D8B24B" },
  { value: "White", swatch: "#D9D9D9" },
  { value: "Rose", swatch: "#E3AFA0" },
];
const RING_SIZES = Array.from({ length: 15 }, (_, i) => 8 + i); // 8..22
const MANGALSUTRA_LENGTHS = [14, 16, 18, 20, 22];

/**
 * "Customize Your Piece" — sits directly above the Price Breakdown on the PDP. Lets the shopper
 * change Metal Purity, Gold Color, Diamond Type and (for sizable categories) Ring Size /
 * Mangalsutra Length. Every change is debounced and re-priced server-side via
 * POST /products/{id}/customize (never computed client-side — the pricing engine is the single
 * source of truth), and the result (pricing + stock/default-combo meta) is lifted to the parent
 * PDP so it can update <PriceBreakdown> and swap the Add to Cart / Request Custom Build button.
 */
export function CustomizeYourPiece({ product, onChange }) {
  const hasGold = (product?.gold?.weight_g || 0) > 0;
  const hasDiamonds = (product?.diamonds || []).length > 0;
  const category = (product?.category || "").trim().toLowerCase();
  const isRing = category === "rings";
  const isMangalsutra = category === "mangalsutra";
  const originalDiamondType = hasDiamonds ? (product.diamonds[0].diamond_type || "natural") : null;

  const [karat, setKarat] = useState(product?.gold?.karat || "22K");
  const [goldColor, setGoldColor] = useState(product?.gold?.color || "Yellow");
  const [diamondType, setDiamondType] = useState(originalDiamondType || "natural");
  const [ringSize, setRingSize] = useState(product?.default_ring_size || 15);
  const [length, setLength] = useState(product?.default_mangalsutra_length_in || 18);
  const [loading, setLoading] = useState(false);
  const [diamondAvailability, setDiamondAvailability] = useState({ natural: true, lab: true });
  const debounceRef = useRef(null);

  // Reset selectors whenever a different product loads.
  useEffect(() => {
    setKarat(product?.gold?.karat || "22K");
    setGoldColor(product?.gold?.color || "Yellow");
    setDiamondType(originalDiamondType || "natural");
    setRingSize(product?.default_ring_size || 15);
    setLength(product?.default_mangalsutra_length_in || 18);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const body = {
        karat, gold_color: goldColor,
        diamond_type: hasDiamonds ? diamondType : undefined,
        ring_size: isRing ? ringSize : undefined,
        mangalsutra_length_in: isMangalsutra ? length : undefined,
      };
      const { data } = await api.post(`/products/${product.id}/customize`, body);
      setDiamondAvailability(data.diamond_type_available || { natural: true, lab: true });
      // If our selection turned out unavailable (lab with no label-match), snap the UI back.
      if (hasDiamonds && data.resolved?.diamond_type && data.resolved.diamond_type !== diamondType) {
        setDiamondType(data.resolved.diamond_type);
      }
      onChange(data.pricing, {
        isDefaultCombo: data.is_default_combo,
        inStock: data.in_stock,
        productionDays: data.production_days,
      });
    } catch {
      // Re-pricing is non-critical to keep the page usable — fall back silently to the last
      // known-good pricing already shown; the user can retry by tweaking a selector again.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!product?.id) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchPricing, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, karat, goldColor, diamondType, ringSize, length]);

  if (!hasGold && !hasDiamonds) return null;

  return (
    <div data-testid="pdp-customize-section" className="rounded-xl border border-border bg-card p-4 sm:p-5 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-4 w-4 text-[hsl(var(--gold-dark))]" />
        <h2 className="font-display text-lg">Customize Your Piece</h2>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-auto" />}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {hasGold && (
          <div>
            <Label className="text-xs text-muted-foreground">Metal Purity</Label>
            <Select value={karat} onValueChange={setKarat}>
              <SelectTrigger data-testid="customize-purity-select" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{KARATS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        {hasGold && (
          <div>
            <Label className="text-xs text-muted-foreground">Gold Color</Label>
            <div className="flex items-center gap-2 mt-1.5">
              {GOLD_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`customize-color-${c.value.toLowerCase()}`}
                  onClick={() => setGoldColor(c.value)}
                  title={`${c.value} Gold`}
                  className={`h-9 flex-1 min-w-0 rounded-lg border flex items-center justify-center gap-1.5 text-xs transition-[border-color,background-color] duration-150 ${
                    goldColor === c.value
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--gold-light))] font-medium"
                      : "border-border hover:bg-[#F2E8DD]"
                  }`}
                >
                  <span className="h-3.5 w-3.5 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: c.swatch }} />
                  <span className="truncate">{c.value}</span>
                  {goldColor === c.value && <Check className="h-3 w-3 text-[hsl(var(--primary))] shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {hasDiamonds && (
          <div className={isRing || isMangalsutra ? "" : "sm:col-span-2"}>
            <Label className="text-xs text-muted-foreground">Diamond Type</Label>
            <div className="flex items-center gap-2 mt-1.5">
              {[{ value: "natural", label: "Natural" }, { value: "lab", label: "Lab-Grown" }].map((t) => {
                const available = diamondAvailability[t.value];
                return (
                  <button
                    key={t.value}
                    type="button"
                    data-testid={`customize-diamond-type-${t.value}`}
                    disabled={!available}
                    title={!available ? "Not available for this design's diamond grade" : ""}
                    onClick={() => available && setDiamondType(t.value)}
                    className={`h-9 flex-1 min-w-0 rounded-lg border text-xs px-1 truncate transition-[border-color,background-color,opacity] duration-150 ${
                      diamondType === t.value
                        ? "border-[hsl(var(--primary))] bg-[hsl(var(--gold-light))] font-medium"
                        : "border-border hover:bg-[#F2E8DD]"
                    } ${!available ? "opacity-40 cursor-not-allowed hover:bg-transparent" : ""}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isRing && (
          <div>
            <Label className="text-xs text-muted-foreground">Ring Size (Indian)</Label>
            <Select value={String(ringSize)} onValueChange={(v) => setRingSize(Number(v))}>
              <SelectTrigger data-testid="customize-ring-size-select" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{RING_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        {isMangalsutra && (
          <div>
            <Label className="text-xs text-muted-foreground">Length (inches)</Label>
            <Select value={String(length)} onValueChange={(v) => setLength(Number(v))}>
              <SelectTrigger data-testid="customize-mangalsutra-length-select" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{MANGALSUTRA_LENGTHS.map((l) => <SelectItem key={l} value={String(l)}>{l}"</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}
